import {useState, useEffect, useCallback, useMemo, useRef} from "react";
import {runInAction} from "mobx";
import {XyiconFeature} from "../../../generated/api/base";
import type {AppState} from "../../../data/state/AppState";
import type {IFilterRow} from "../../../data/models/filter/Filter";
import type {ISpotlightRule} from "../../../data/models/View";
import {ObjectUtils} from "../../../utils/data/ObjectUtils";
import {
	constructFilterTitle,
	isFilterComplete,
	cleanSpotlightRulesForComparison,
	getNextDefaultColor,
	removeDeletedFieldsFromRules,
} from "./spotlightUtils";

type SpotlightRuleType = "xyiconSpotlightRules" | "boundarySpotlightRules" | "listViewSpotlightRules";

interface IUseSpotlightRulesOptions {
	feature: XyiconFeature;
	appState: AppState;
	ruleType: SpotlightRuleType;
}

export const useSpotlightRules = (options: IUseSpotlightRulesOptions) => {
	const {feature, appState, ruleType} = options;

	const getCurrentRules = useCallback((): ISpotlightRule[] => {
		const selectedView = appState.actions.getSelectedView(feature);
		const spotlightRules = selectedView?.spotlightRules;
		const currentRules = (spotlightRules?.[ruleType] || []) as ISpotlightRule[];
		return removeDeletedFieldsFromRules(ObjectUtils.deepClone(currentRules), appState);
	}, [feature, ruleType, appState]);

	const getSavedRules = useCallback((): ISpotlightRule[] => {
		const selectedView = appState.actions.getSelectedView(feature);
		const savedAdditionalDetails = JSON.parse(selectedView?.data?.additionalDetails || "{}");
		const savedRules = (savedAdditionalDetails.spotlightRules?.[ruleType] || []) as ISpotlightRule[];
		return removeDeletedFieldsFromRules(ObjectUtils.deepClone(savedRules), appState);
	}, [feature, ruleType, appState]);

	const [rules, setRules] = useState<ISpotlightRule[]>(getCurrentRules);
	const [savedRules, setSavedRules] = useState<ISpotlightRule[]>(getSavedRules);

	const rulesRef = useRef<ISpotlightRule[]>(rules);
	const isFirstRenderRef = useRef(true);

	useEffect(() => {
		rulesRef.current = rules;
	}, [rules]);

	useEffect(() => {
		/* eslint-disable react-hooks/set-state-in-effect -- this hook intentionally refreshes local editable state when the selected view context changes. */
		setRules(getCurrentRules());
		setSavedRules(getSavedRules());
		/* eslint-enable react-hooks/set-state-in-effect */
	}, [feature, ruleType, getCurrentRules, getSavedRules]);

	const cleanRulesForView = useCallback((rulesToClean: ISpotlightRule[]): ISpotlightRule[] => {
		return rulesToClean.map((rule) => {
			// Filter out incomplete filters before syncing to view
			if (rule.filters?.length > 0) {
				const completeFilters = rule.filters.filter((filter) => filter.type === "separator" || isFilterComplete(filter));
				while (completeFilters.length > 0 && completeFilters[completeFilters.length - 1]?.type === "separator") {
					completeFilters.pop();
				}
				return {...rule, filters: completeFilters};
			}
			return rule;
		});
	}, []);

	const syncRulesToView = useCallback(
		(rulesToSync: ISpotlightRule[]) => {
			runInAction(() => {
				const selectedView = appState.actions.getSelectedView(feature);
				if (!selectedView) {
					return;
				}

				const cleanedRules = cleanRulesForView(rulesToSync);

				const currentSpotlightRules = selectedView.spotlightRules || {
					xyiconSpotlightRules: [],
					boundarySpotlightRules: [],
					listViewSpotlightRules: [],
				};

				selectedView.spotlightRules = {
					...currentSpotlightRules,
					[ruleType]: ObjectUtils.deepClone(cleanedRules),
				};

				if (feature === XyiconFeature.SpaceEditor) {
					const manager = appState.app.spaceViewRenderer?.getItemManager(
						ruleType === "xyiconSpotlightRules" ? XyiconFeature.Xyicon : XyiconFeature.Boundary,
					);
					manager?.onFormattingRulesModified();
				}
			});
		},
		[feature, ruleType, appState, cleanRulesForView],
	);

	useEffect(() => {
		if (isFirstRenderRef.current) {
			isFirstRenderRef.current = false;
			return;
		}
		syncRulesToView(rules);
	}, [rules, syncRulesToView]);

	const addRule = useCallback(
		(ruleTypeParam: "conditional" | "field" = "conditional") => {
			// Create the rule BEFORE setRules to avoid timing issues
			const defaultFilters: IFilterRow[] =
				ruleTypeParam === "conditional" ? [{type: "filter", value: {field: "", operator: "" as any, param: ""}}] : [];

			const createdRule: ISpotlightRule = {
				id: `rule-${Date.now()}`,
				label: "Define Spotlight Rule",
				color: getNextDefaultColor(rulesRef.current.length),
				filters: defaultFilters,
				ruleType: ruleTypeParam,
				fieldRefID: "",
				highlightType: "row",
				indicatorType: "highlight",
			};

			// Set new rule as expanded in localStorage (default for new rules)
			const storageKey = `spotlight-rule-expanded-${ruleType}-${createdRule.id}`;
			localStorage.setItem(storageKey, "true");

			setRules((prevRules) => {
				const updatedRules = [...prevRules, createdRule];
				rulesRef.current = updatedRules;
				return updatedRules;
			});

			return createdRule;
		},
		[ruleType],
	);

	const updateRule = useCallback(
		(ruleId: string, updates: Partial<ISpotlightRule>) => {
			const currentRules = rulesRef.current;

			if (!currentRules.some((rule) => rule.id === ruleId)) {
				return;
			}

			const updatedRules = currentRules.map((rule: ISpotlightRule) => {
				if (rule.id === ruleId) {
					const merged = {...rule, ...updates};

					// For conditional rules, always recompute the label from filters to ensure consistency
					if (merged.ruleType === "conditional" && merged.filters) {
						const filterTitle = constructFilterTitle(merged.filters, appState);
						merged.label = filterTitle || "Define Spotlight Rule";
					}

					// For field-based rules, construct label from fieldRefID and filters
					if (merged.ruleType === "field" && merged.fieldRefID) {
						const fieldTitle = appState.actions.getFieldTitle(merged.fieldRefID);
						const baseLabel = fieldTitle ? `Color by ${fieldTitle}` : "Define Spotlight";

						if (merged.filters && merged.filters.length > 0) {
							const completeFilters = merged.filters.filter((filter: IFilterRow) => {
								if (filter.type === "separator") {
									return true;
								}
								return filter.type === "filter" && filter.value.field && filter.value.operator;
							});
							const filterTitle = constructFilterTitle(completeFilters, appState);
							merged.label = filterTitle ? `${baseLabel} where ${filterTitle}` : baseLabel;
						} else {
							merged.label = baseLabel;
						}
					}

					return merged;
				}
				return rule;
			});
			rulesRef.current = updatedRules;
			setRules(updatedRules);
		},
		[appState],
	);

	const removeRule = useCallback((ruleId: string) => {
		setRules((prevRules) => {
			const updatedRules = prevRules.filter((rule) => rule.id !== ruleId);
			rulesRef.current = updatedRules;
			return updatedRules;
		});
	}, []);

	const reorderRules = useCallback((reorderedRules: ISpotlightRule[]) => {
		rulesRef.current = reorderedRules;
		setRules(reorderedRules);
	}, []);

	const saveRules = useCallback(() => {
		const clonedRules = ObjectUtils.deepClone(rulesRef.current);
		setSavedRules(clonedRules);
		syncRulesToView(clonedRules);
	}, [syncRulesToView]);

	const resetRules = useCallback(() => {
		const clonedSavedRules = ObjectUtils.deepClone(savedRules);
		rulesRef.current = clonedSavedRules;
		setRules(clonedSavedRules);
		syncRulesToView(clonedSavedRules);
	}, [savedRules, syncRulesToView]);

	const clearAllRules = useCallback(() => {
		rulesRef.current = [];
		setRules([]);
	}, []);

	const updateRuleColor = useCallback((ruleId: string, color: string) => updateRule(ruleId, {color}), [updateRule]);
	const updateRuleFilters = useCallback((ruleId: string, filters: IFilterRow[]) => updateRule(ruleId, {filters}), [updateRule]);
	const constructFilterTitleFromHook = useCallback((filters: IFilterRow[]): string => constructFilterTitle(filters, appState), [appState]);

	// Normalize rules to compute labels from filters for consistent comparison
	const normalizeRulesForComparison = useCallback(
		(rulesToNormalize: ISpotlightRule[]): ISpotlightRule[] =>
			rulesToNormalize.map((rule) => {
				if (rule.ruleType === "conditional" && rule.filters) {
					// Only use complete filters when computing label to avoid false change detection
					const completeFilters = rule.filters.filter((filter) => {
						if (filter.type === "separator") {
							return true;
						}
						return filter.type === "filter" && isFilterComplete(filter);
					});
					const label = constructFilterTitle(completeFilters, appState) || "Define Spotlight Rule";
					return {...rule, label};
				}
				if (rule.ruleType === "field" && rule.fieldRefID) {
					const fieldTitle = appState.actions.getFieldTitle(rule.fieldRefID);
					const baseLabel = fieldTitle ? `Color by ${fieldTitle}` : "Define Spotlight";

					if (rule.filters && rule.filters.length > 0) {
						// Only use complete filters when computing label
						const completeFilters = rule.filters.filter((filter) => {
							if (filter.type === "separator") {
								return true;
							}
							return filter.type === "filter" && isFilterComplete(filter);
						});
						const filterTitle = constructFilterTitle(completeFilters, appState);
						const label = filterTitle ? `${baseLabel} where ${filterTitle}` : baseLabel;
						return {...rule, label};
					}
					return {...rule, label: baseLabel};
				}
				return rule;
			}),
		[appState],
	);

	// Normalize both rules and savedRules before comparison to handle label differences
	const hasUnsavedChanges = useMemo(() => {
		const cleanedRules = cleanSpotlightRulesForComparison(normalizeRulesForComparison(rules));
		const cleanedSavedRules = cleanSpotlightRulesForComparison(normalizeRulesForComparison(savedRules));
		return !ObjectUtils.compare(cleanedRules, cleanedSavedRules);
	}, [rules, savedRules, normalizeRulesForComparison]);

	return {
		rules,
		savedRules,
		hasUnsavedChanges,
		ruleCount: rules.length,
		addRule,
		updateRule,
		updateRuleColor,
		updateRuleFilters,
		removeRule,
		reorderRules,
		saveRules,
		resetRules,
		clearAllRules,
		constructFilterTitle: constructFilterTitleFromHook,
	};
};
